// Write-offs (D-049): what was binned, why, and what it cost, for a period of trading days
// (the shared DateField; the last 7 days by default). The report totals the period in rand
// by reason, product or day (reversed write-offs left out); the list below shows each
// write-off with its approval. Both export as CSV.

import { type WriteOff, type WriteOffReport, WRITE_OFF_REPORT_GROUPS } from '@enp/domain/api';
import { formatCount } from '@enp/domain/format';
import { WRITE_OFF_REASONS } from '@enp/domain/stock-control';
import { addDays } from '@enp/domain/time';
import {
  Badge,
  Button,
  cn,
  createColumnHelper,
  DataTable,
  EmptyState,
  focusRing,
  Money,
  PageHeader,
  Qty,
  Skeleton,
  StatusBadge,
  toast,
  type ColumnDef,
} from '@enp/ui';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Download, PackageX, SearchX, Trash2, X } from 'lucide-react';
import { useMemo } from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { downloadText } from '../../../replay/csv.js';
import { AccountDate } from '../../../shell/account-date.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { everyDay, writeOffReportCsv, writeOffsCsv } from '../../../stock-control/csv.js';
import { actionProblem } from '../../../stock-control/parts.js';
import {
  allWriteOffs,
  writeOffPeriod,
  writeOffReportQuery,
  writeOffsQuery,
  writeOffsSearchSchema,
  type WriteOffsSearch,
} from '../../../stock-control/queries.js';
import { qtyWithUnit, REASON_LABEL, writeOffStatus } from '../../../stock-control/words.js';
import {
  DayRange,
  FilterSelect,
  LoadMoreFooter,
  StockStateBadge,
  StockViews,
} from '../../../stock/components.js';
import { plainDateLabel, accountToday } from '../../../stock/labels.js';
import { clean, lotsQuery } from '../../../stock/queries.js';

export const Route = createFileRoute('/_app/stock/write-offs')({
  validateSearch: writeOffsSearchSchema,
  component: WriteOffsPage,
});

const TABLE_LABEL = 'Write-offs';

const GROUP_LABEL: Record<WriteOffReport['group_by'], string> = {
  reason: 'By reason',
  product: 'By product',
  day: 'By day',
};

/** "21 Sep – 27 Sep", or one day. */
function periodText(from: string, to: string, today: string): string {
  return from === to
    ? plainDateLabel(from, today)
    : `${plainDateLabel(from, today)} – ${plainDateLabel(to, today)}`;
}

// ---------------------------------------------------------------- the report

function ReportTable({ report: given, today }: { report: WriteOffReport; today: string }) {
  // By day: every day of the period, quiet ones too (UI4A-13).
  const report = everyDay(given);
  const max = Math.max(1, ...report.rows.map((r) => r.value_cents));
  const label = (r: WriteOffReport['rows'][number]) =>
    report.group_by === 'day' ? plainDateLabel(r.key, today) : r.label;
  return (
    <table
      aria-label={`Write-offs ${GROUP_LABEL[report.group_by].toLowerCase()}`}
      className="w-full text-body"
    >
      <thead>
        <tr className="border-b border-border text-left text-sm text-muted">
          <th scope="col" className="py-2 pr-4 font-medium">
            {report.group_by === 'reason'
              ? 'Reason'
              : report.group_by === 'product'
                ? 'Product'
                : 'Trading day'}
          </th>
          <th scope="col" className="py-2 pr-4 text-right font-medium">
            Write-offs
          </th>
          {report.group_by === 'product' && (
            <th scope="col" className="py-2 pr-4 text-right font-medium">
              Quantity
            </th>
          )}
          <th scope="col" className="py-2 pr-4 text-right font-medium">
            Value
          </th>
          <th scope="col" className="hidden w-2/5 py-2 font-medium md:table-cell">
            <span className="sr-only">Share of the value</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {report.rows.map((r) => (
          <tr key={r.key} className="border-b border-border last:border-0">
            <th scope="row" className="py-2 pr-4 text-left font-medium text-fg">
              {label(r)}
              {r.uncosted_count > 0 && (
                <span className="block text-xs font-normal text-muted">
                  {formatCount(r.uncosted_count)} without a cost, not in the value
                </span>
              )}
            </th>
            <td className="py-2 pr-4 text-right tabular-nums text-fg">{formatCount(r.count)}</td>
            {report.group_by === 'product' && (
              <td className="whitespace-nowrap py-2 pr-4 text-right tabular-nums">
                {/* With its unit: products are counted in different units (UI4A-9). */}
                {r.qty !== null ? qtyWithUnit(r.qty, r.unit ?? '', 6) : '—'}
              </td>
            )}
            <td className="py-2 pr-4 text-right">
              <Money cents={r.value_cents} className="font-semibold text-fg" />
            </td>
            <td className="hidden py-2 md:table-cell" aria-hidden>
              <progress
                value={Math.round((r.value_cents / max) * 100)}
                max={100}
                className={cn(
                  'block h-2 w-full appearance-none overflow-hidden rounded-full bg-surface-sunken',
                  '[&::-webkit-progress-bar]:bg-surface-sunken [&::-webkit-progress-value]:rounded-full [&::-webkit-progress-value]:bg-primary [&::-moz-progress-bar]:bg-primary',
                )}
              />
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-border-strong">
          <th scope="row" className="py-2 pr-4 text-left font-semibold text-fg">
            Total
          </th>
          <td className="py-2 pr-4 text-right font-semibold tabular-nums text-fg">
            {formatCount(report.total_count)}
          </td>
          {report.group_by === 'product' && <td />}
          <td className="py-2 pr-4 text-right">
            <Money cents={report.total_value_cents} className="font-semibold text-fg" />
          </td>
          <td className="hidden md:table-cell" />
        </tr>
      </tfoot>
    </table>
  );
}

function Report({
  search,
  from,
  to,
  today,
  onGroup,
}: {
  search: WriteOffsSearch;
  from: string;
  to: string;
  today: string;
  onGroup: (g: WriteOffReport['group_by']) => void;
}) {
  const q = useQuery({
    ...writeOffReportQuery(search, from, to),
    placeholderData: keepPreviousData,
  });
  const report = q.data;
  const group = search.group ?? 'reason';
  return (
    <section
      aria-labelledby="wo-report"
      className="mb-6 flex flex-col gap-4 rounded-lg border border-border bg-surface-raised p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 id="wo-report" className="font-display text-lg font-semibold text-fg">
            Written off {periodText(from, to, today)}
          </h2>
          {report ? (
            <p className="flex flex-wrap items-baseline gap-x-2 text-body text-muted">
              <Money
                cents={report.total_value_cents}
                className="font-display text-2xl font-semibold text-fg"
              />
              <span>
                in {formatCount(report.total_count)}{' '}
                {report.total_count === 1 ? 'write-off' : 'write-offs'}
                {report.uncosted_count > 0 &&
                  `; ${formatCount(report.uncosted_count)} without a cost not counted in rand`}
              </span>
            </p>
          ) : (
            <Skeleton className="h-8 w-56" />
          )}
          <p className="text-sm text-muted">At standard cost. Reversed write-offs are left out.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="group"
            aria-label="Group the report"
            className="inline-flex rounded-md border border-border-strong p-0.5"
          >
            {WRITE_OFF_REPORT_GROUPS.map((g) => (
              <button
                key={g}
                type="button"
                aria-pressed={g === group}
                onClick={() => onGroup(g)}
                className={cn(
                  'h-control-sm rounded-sm px-3 text-sm font-medium text-muted hover:text-fg',
                  g === group && 'bg-primary-subtle text-primary-text',
                  focusRing,
                )}
              >
                {GROUP_LABEL[g]}
              </button>
            ))}
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download aria-hidden />}
            // Not while a new period or grouping loads: the file would be the old one (UI4A-13).
            disabled={!report || q.isFetching}
            onClick={() =>
              report &&
              downloadText(
                `write-off-report-by-${group}-${from}-to-${to}.csv`,
                writeOffReportCsv(report),
              )
            }
          >
            Download report (CSV)
          </Button>
        </div>
      </div>
      {q.isError && !report ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The report"
          onRetry={() => void q.refetch()}
        />
      ) : !report ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span role="status" className="sr-only">
            Loading the report…
          </span>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-6 w-full" />
          ))}
        </div>
      ) : report.rows.length === 0 ? (
        <p className="text-body text-muted">Nothing was written off in these days.</p>
      ) : (
        <ReportTable report={report} today={today} />
      )}
    </section>
  );
}

// ---------------------------------------------------------------- the list

function columns(): ColumnDef<WriteOff, unknown>[] {
  const col = createColumnHelper<WriteOff>();
  return [
    col.display({
      id: 'when',
      header: 'When',
      cell: ({ row }) => (
        <span className="flex flex-col whitespace-nowrap leading-tight">
          <AccountDate value={row.original.occurred_at} withTime className="text-fg" />
          <span className="font-mono text-xs text-muted">{row.original.number}</span>
        </span>
      ),
    }),
    col.display({
      id: 'product',
      header: 'Product',
      meta: { width: 'min-w-48' },
      cell: ({ row }) => {
        const w = row.original;
        return (
          <span className="flex flex-col gap-1 leading-tight">
            <Link
              to="/stock/$productId"
              params={{ productId: w.product.id }}
              className="font-medium text-fg hover:underline"
            >
              {w.product.name}
            </Link>
            <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
              {w.lot && <span className="font-mono">{w.lot.lot_code}</span>}
              <StockStateBadge state={w.state} />
              <span>{w.location.name}</span>
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'qty',
      header: 'Quantity',
      meta: { align: 'right' },
      cell: ({ row }) => (
        <Qty
          value={row.original.qty}
          unit={row.original.product.base_uom}
          className="font-semibold text-fg"
        />
      ),
    }),
    col.display({
      id: 'reason',
      header: 'Why',
      cell: ({ row }) => (
        <span className="flex flex-col leading-tight">
          <span className="text-fg">{REASON_LABEL[row.original.reason]}</span>
          {row.original.note && (
            <span className="max-w-56 truncate text-xs text-muted" title={row.original.note}>
              “{row.original.note}”
            </span>
          )}
        </span>
      ),
    }),
    col.display({
      id: 'value',
      header: 'Value',
      meta: { align: 'right' },
      cell: ({ row }) =>
        row.original.value_cents === null ? (
          <span className="whitespace-nowrap text-sm text-muted">No cost</span>
        ) : (
          <Money cents={row.original.value_cents} className="font-medium text-fg" />
        ),
    }),
    col.display({
      id: 'who',
      header: 'Recorded by',
      meta: { className: 'hidden lg:table-cell' },
      cell: ({ row }) => (
        <span className="text-fg">{row.original.recorded_by?.name ?? 'Unknown person'}</span>
      ),
    }),
    col.display({
      id: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const s = writeOffStatus(row.original);
        const a = row.original.approval;
        return a && a.mode === 'async' ? (
          <Link
            to="/approvals"
            search={{ view: a.status === 'requested' ? undefined : a.status, id: a.id }}
            className="rounded-full"
            aria-label={`${s.label}: open in Approvals`}
          >
            <StatusBadge status={s.status}>{s.label}</StatusBadge>
          </Link>
        ) : (
          <StatusBadge status={s.status}>{s.label}</StatusBadge>
        );
      },
    }),
  ];
}

function WriteOffsPage() {
  useDocumentTitle('Write-offs');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const { from, to } = writeOffPeriod(search, addDays(today, -6), today);
  const setSearch = (patch: Partial<WriteOffsSearch>) =>
    void navigate({ search: (prev) => clean({ ...prev, ...patch }), replace: true });

  const product = useQuery({
    ...lotsQuery(search.product ?? ''),
    enabled: Boolean(search.product),
  });
  const list = useInfiniteQuery({
    ...writeOffsQuery(search, from, to),
    placeholderData: keepPreviousData,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const filtered = Boolean(search.reason || search.product || search.status);

  const exportAll = useMutation({
    mutationFn: () => allWriteOffs(search, from, to),
    onSuccess: ({ items: rows, truncated }) => {
      downloadText(`write-offs-list-${from}-to-${to}.csv`, writeOffsCsv(rows));
      toast.success('CSV downloaded', {
        description: truncated
          ? `The first ${formatCount(rows.length)} write-offs; choose fewer days for the rest.`
          : `${formatCount(rows.length)} ${rows.length === 1 ? 'write-off' : 'write-offs'}.`,
      });
    },
    onError: (error) => {
      const p = actionProblem(error, 'The CSV wasn’t made');
      toast.error(p.title, { description: p.explain });
    },
  });

  const clearFilters = () =>
    setSearch({ reason: undefined, product: undefined, status: undefined });

  const empty = filtered ? (
    <EmptyState
      compact
      icon={SearchX}
      title="No write-offs match"
      description="Nothing written off in these days fits the filters."
      action={
        <Button variant="secondary" icon={<X aria-hidden />} onClick={clearFilters}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={PackageX}
      title="Nothing written off in these days"
      description="Write-offs are recorded at a station when stock is binned: expired, damaged or not good enough to sell. Pick earlier days to see older ones."
    />
  );

  return (
    <>
      <PageHeader
        title="Stock"
        description="What was binned, why, and what it cost at standard cost."
      />
      <StockViews current="write-offs" />

      <div
        className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2"
        role="search"
        aria-label="Write-off period"
      >
        <DayRange
          idPrefix="write-offs"
          from={from}
          to={to}
          today={today}
          onChange={(r) => setSearch({ from: r.from, to: r.to })}
        />
        {(search.from || search.to) && (
          <Button
            variant="ghost"
            icon={<X aria-hidden />}
            onClick={() => setSearch({ from: undefined, to: undefined })}
          >
            Last 7 days
          </Button>
        )}
      </div>

      <Report
        search={search}
        from={from}
        to={to}
        today={today}
        onGroup={(g) => setSearch({ group: g === 'reason' ? undefined : g })}
      />

      <section aria-labelledby="wo-list" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="wo-list" className="font-display text-lg font-semibold text-fg">
            Each write-off
          </h2>
          <Button
            variant="secondary"
            icon={<Download aria-hidden />}
            loading={exportAll.isPending}
            disabled={list.isPending}
            onClick={() => exportAll.mutate()}
          >
            Download list (CSV)
          </Button>
        </div>
        <div
          className="flex flex-wrap items-center gap-2"
          role="search"
          aria-label="Write-off filters"
        >
          <FilterSelect
            label="Why"
            value={search.reason}
            allLabel="Every reason"
            options={WRITE_OFF_REASONS.map((r) => ({ value: r, label: REASON_LABEL[r] }))}
            onChange={(v) => setSearch({ reason: v as WriteOffsSearch['reason'] })}
          />
          <FilterSelect
            label="Status"
            value={search.status}
            allLabel="Written off or reversed"
            options={[
              { value: 'applied', label: 'Written off' },
              { value: 'reversed', label: 'Reversed' },
            ]}
            onChange={(v) => setSearch({ status: v as WriteOffsSearch['status'] })}
          />
          {search.product && (
            <Badge tone="neutral" className="h-control-sm gap-1.5 pr-1">
              <Trash2 aria-hidden />
              {product.data?.product.name ?? 'One product'}
              <button
                type="button"
                aria-label="Show every product"
                onClick={() => setSearch({ product: undefined })}
                className="rounded-sm p-0.5 hover:bg-surface-hover"
              >
                <X aria-hidden className="size-3.5" />
              </button>
            </Badge>
          )}
          {filtered && (
            <Button variant="ghost" icon={<X aria-hidden />} onClick={clearFilters}>
              Clear
            </Button>
          )}
        </div>
        {list.isError && !list.data ? (
          <ErrorPanel
            error={list.error}
            titleAs="h2"
            what="The write-offs"
            onRetry={() => void list.refetch()}
          />
        ) : (
          <DataTable
            label={TABLE_LABEL}
            columns={columns()}
            data={items}
            loading={list.isPending}
            empty={empty}
            filterable={false}
            getRowId={(w) => w.id}
            footer={
              !list.isPending && items.length > 0 ? (
                <LoadMoreFooter
                  loaded={items.length}
                  noun={['write-off', 'write-offs']}
                  hasMore={list.hasNextPage}
                  loading={list.isFetchingNextPage}
                  updating={list.isFetching && !list.isFetchingNextPage}
                  failed={list.isError && Boolean(list.data)}
                  onMore={() => void list.fetchNextPage()}
                />
              ) : undefined
            }
          />
        )}
      </section>
    </>
  );
}
